import { useState } from 'react';
import { calculatePrepaymentAnalysis, formatCurrency } from '../utils/mortgageCalculations';

function PrepaymentCalculator({ loanData, calculatedData, prepaymentData, setPrepaymentData }) {
    // derived alias for cleaner code if needed, but using direct prop is better for writes
    const extraPayment = prepaymentData.extraPayment;

    const analysis = calculatePrepaymentAnalysis(
        calculatedData.loanAmount,
        loanData.interestRate,
        loanData.loanYears,
        parseFloat(extraPayment) || 0,
        loanData.homePrice,
        loanData.downPaymentPercent,
        calculatedData.pmi  // Pass manual PMI override
    );

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Prepayment Calculator</h3>
            </div>

            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                See how making extra principal payments can save you money and reduce your loan term.
            </p>

            <div className="form-group">
                <label className="form-label">Additional Monthly Principal Payment</label>
                <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input
                        type="number"
                        className="form-input"
                        value={extraPayment}
                        onChange={(e) => setPrepaymentData({ ...prepaymentData, extraPayment: e.target.value })}
                        min="0"
                        step="50"
                        placeholder="0"
                    />
                </div>
            </div>

            <div className="grid grid-2">
                {/* Original Loan */}
                <div className="card" style={{ background: 'var(--bg-tertiary)', paddingLeft: 'var(--spacing-lg)' }}>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-md)' }}>
                        Original Loan
                    </h4>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                        <div>
                            <div className="summary-label">Loan Term</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                                {analysis.original.years} years
                            </div>
                            <div className="summary-subtext">{analysis.original.months} months</div>
                        </div>

                        <div>
                            <div className="summary-label">Total Interest Paid</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                {formatCurrency(analysis.original.totalInterest)}
                            </div>
                            <div className="summary-subtext">{analysis.original.interestPercent}% of loan amount</div>
                        </div>

                        {calculatedData.pmi > 0 && (
                            <>
                                <div>
                                    <div className="summary-label">Total PMI Paid</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                        {formatCurrency(analysis.original.totalPMI)}
                                    </div>
                                </div>

                                <div>
                                    <div className="summary-label">PMI Removal Date</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                        {analysis.original.pmiRemovalDate}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Revised Loan with Prepayment */}
                <div className="card" style={{ background: 'var(--bg-tertiary)', border: '2px solid var(--primary-color)', paddingLeft: 'var(--spacing-lg)' }}>
                    <h4 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
                        With Extra ${parseFloat(extraPayment) || 0}/month
                    </h4>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                        <div>
                            <div className="summary-label">Loan Term</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-color)' }}>
                                {analysis.revised.years} years
                            </div>
                            <div className="summary-subtext">{analysis.revised.months} months</div>
                            {analysis.savings.months > 0 && (
                                <div className="badge badge-success" style={{ marginTop: '0.5rem' }}>
                                    Save {analysis.savings.years} years
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="summary-label">Total Interest Paid</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary-color)' }}>
                                {formatCurrency(analysis.revised.totalInterest)}
                            </div>
                            <div className="summary-subtext">{analysis.revised.interestPercent}% of loan amount</div>
                            {analysis.savings.interest > 0 && (
                                <div className="badge badge-success" style={{ marginTop: '0.5rem' }}>
                                    Save {formatCurrency(analysis.savings.interest)}
                                </div>
                            )}
                        </div>

                        {calculatedData.pmi > 0 && (
                            <>
                                <div>
                                    <div className="summary-label">Total PMI Paid</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary-color)' }}>
                                        {formatCurrency(analysis.revised.totalPMI)}
                                    </div>
                                    {analysis.savings.pmi > 0 && (
                                        <div className="badge badge-success" style={{ marginTop: '0.5rem' }}>
                                            Save {formatCurrency(analysis.savings.pmi)}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="summary-label">PMI Removal Date</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--primary-color)' }}>
                                        {analysis.revised.pmiRemovalDate}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Total Savings Summary */}
            {analysis.savings.total > 0 && (
                <div className="card" style={{ marginTop: 'var(--spacing-md)', background: 'var(--primary-gradient)', color: 'white' }}>
                    <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'white' }}>Total Savings</h3>
                    <div className="grid grid-3">
                        <div>
                            <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Time Saved</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                                {analysis.savings.years} years
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Interest Saved</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                                {formatCurrency(analysis.savings.interest)}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Saved</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                                {formatCurrency(analysis.savings.total)}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PrepaymentCalculator;
